"use client";

import { useEffect, useState } from "react";

import { useRouter } from "@bprogress/next";
import { usePathname, useSearchParams } from "next/navigation";
import { useInView } from "react-intersection-observer";
import { LoaderIcon } from "lucide-react";

export function InfiniteScroll({
  pagination,
  limitKey = "limit",
  increment = 48,
  initialLimit = 96,
}: {
  pagination: Pagination<any>;
  limitKey?: string;
  increment?: number;
  initialLimit?: number;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [rootElement, setRootElement] = useState(null);

  useEffect(() => {
    const updateRootElement = () => {
      if (window.innerWidth >= 1280) {
        const element = document.querySelector(
          "#rmd-v2-wrapper",
        ) as HTMLElement;
        setRootElement(element);
      } else {
        setRootElement(null);
      }
    };

    updateRootElement();
    window.addEventListener("resize", updateRootElement);

    return () => {
      window.removeEventListener("resize", updateRootElement);
    };
  }, []);

  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: "600px",
    root: rootElement,
  });

  const [haveToPaginate, setHaveToPaginate] = useState(false);

  useEffect(() => {
    if (inView && haveToPaginate) {
      // Operate on URLSearchParams directly so multi-value params (e.g.
      // `specializations`) are preserved instead of collapsed to their last value.
      const params = new URLSearchParams(searchParams as any);
      const current = params.get(limitKey);

      params.set(
        limitKey,
        (current ? parseInt(current) + increment : initialLimit).toString(),
      );

      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    }
  }, [inView, haveToPaginate]);

  useEffect(() => {
    const { haveToPaginate } = pagination;

    setHaveToPaginate(haveToPaginate);
  }, [pagination]);

  return haveToPaginate ? (
    <div ref={ref} className={"flex w-full justify-center py-24"}>
      <LoaderIcon className={inView ? "animate-spin" : ""} />
    </div>
  ) : (
    <></>
  );
}
